import { useState } from "react";
import { generatePath } from "react-router";
import { useTranslations } from "@acto/web-common/locales";
import { useDismissCustomer } from "@acto/taskpane/core/data-access/observations/useDismissCustomer";
import { useMarkCustomerDone } from "@acto/taskpane/core/data-access/observations/useMarkCustomerDone";
import { AnalyticsEvents, useAnalyticsSource, useTrack } from "@acto/taskpane/core/analytics";
import ActoRoutes from "@acto/taskpane/core/router/routes.config";
import { useDirectionalNavigate } from "@acto/taskpane/field-sales-2/utils/viewTransitions";
import { toLocalIsoDate } from "@acto/taskpane/field-sales-2/utils/format";
import { PlanMeetingSheet } from "@acto/taskpane/field-sales-2/components/patterns/PlanMeetingSheet";
import { useOpenCreateTodo } from "@acto/taskpane/field-sales-2/components/patterns/CreateTodoSheet";
import { FullScreenErrorModal } from "@acto/taskpane/field-sales-2/components/primitives/FullScreenErrorModal";
import { useSuccessModal } from "@acto/taskpane/field-sales-2/components/primitives/FullScreenSuccessModal";
import { CustomerActionsSheet } from "./CustomerActionsSheet";
import { DismissCustomerSheet, type DismissReason } from "./DismissCustomerSheet";
import { MarkAsDoneSheet, type MarkAsDoneReason } from "./MarkAsDoneSheet";

interface CustomerActionsSheetHostProps {
  clientId: string;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  customerName?: string;
}

/**
 * Connects the customer actions sheet to its mutations (dismiss, mark-done) and follow-up flows
 * (plan meeting, create todo) for one customer. The caller owns the trigger and the open state.
 *
 * The create-todo action delegates to {@link useOpenCreateTodo}, so a `CreateTodoFlowProvider`
 * must be mounted above this component.
 */
export function CustomerActionsSheetHost({
  clientId,
  isOpen,
  onOpenChange,
  customerName,
}: CustomerActionsSheetHostProps) {
  const t = useTranslations("fieldSalesTwo.customerActions");
  const analyticsSource = useAnalyticsSource();
  const [planMeetingOpen, setPlanMeetingOpen] = useState(false);
  const [markAsDoneOpen, setMarkAsDoneOpen] = useState(false);
  const [dismissOpen, setDismissOpen] = useState(false);
  const openCreateTodo = useOpenCreateTodo();
  const navigate = useDirectionalNavigate();
  const { showSuccess } = useSuccessModal();
  const { dismissCustomer, result: dismissResult } = useDismissCustomer();
  const { markCustomerDone, result: markDoneResult } = useMarkCustomerDone();
  const track = useTrack();

  // Failure never closes the sheet; dismissing the error overlay returns to it for the retry.
  const [failedAction, setFailedAction] = useState<"dismiss" | "markAsDone" | null>(null);

  const handleMarkAsDoneOpenChange = (open: boolean) => {
    setMarkAsDoneOpen(open);
    if (!open) onOpenChange(true);
  };

  const handleDismissOpenChange = (open: boolean) => {
    // Closing mid-submit would reopen the actions sheet beneath the success modal of a
    // dismissal that still completes; keep the sheet up until the mutation settles.
    if (!open && dismissResult.fetching) return;

    setDismissOpen(open);
    if (!open) onOpenChange(true);
  };

  const handleDismissConfirm = async (reason: DismissReason, comment?: string) => {
    const dismissed = await dismissCustomer(clientId, reason, comment);
    if (!dismissed) {
      setFailedAction("dismiss");
      return;
    }

    track(AnalyticsEvents.RECOMMENDED_CUSTOMER_DISMISSED, {
      customerId: clientId,
      customerName,
      dismissedUntil: dismissed.dismissedUntil,
      reason,
      source: analyticsSource,
    });
    setDismissOpen(false);
    showSuccess({
      title: t("dismiss.success.title"),
      subtitle: t("dismiss.success.subtitle"),
      hint: t("dismiss.success.continueHint"),
    });
  };

  const handleStartRecap = async (reason: MarkAsDoneReason) => {
    // The visit's meeting is created together with the recap note on submit, so an
    // abandoned recap flow leaves no meeting behind.
    const marked = await markCustomerDone(clientId, reason);
    if (!marked) {
      setFailedAction("markAsDone");
      return;
    }

    track(AnalyticsEvents.CUSTOMER_MARKED_DONE, {
      customerId: clientId,
      reason,
      withRecap: true,
      source: analyticsSource,
    });
    setMarkAsDoneOpen(false);
    navigate(generatePath(ActoRoutes.CUSTOMER_RECAPS, { id: clientId }), {
      direction: "push",
      state: { autoStartAdHocRecap: { scheduledDate: toLocalIsoDate(new Date()) } },
    });
  };

  const handleCompleteWithoutRecap = async (reason: MarkAsDoneReason) => {
    const marked = await markCustomerDone(clientId, reason);
    if (!marked) {
      setFailedAction("markAsDone");
      return;
    }

    track(AnalyticsEvents.CUSTOMER_MARKED_DONE, {
      customerId: clientId,
      reason,
      withRecap: false,
      source: analyticsSource,
    });
    setMarkAsDoneOpen(false);
  };

  return (
    <>
      <CustomerActionsSheet
        isOpen={isOpen}
        onOpenChange={onOpenChange}
        onMarkAsDone={() => setMarkAsDoneOpen(true)}
        onDismiss={() => setDismissOpen(true)}
        onPlanMeeting={() => setPlanMeetingOpen(true)}
        onCreateTodo={openCreateTodo}
      />
      <MarkAsDoneSheet
        isOpen={markAsDoneOpen}
        onOpenChange={handleMarkAsDoneOpenChange}
        onStartRecap={handleStartRecap}
        onCompleteWithoutRecap={handleCompleteWithoutRecap}
        isSubmitting={markDoneResult.fetching}
      />
      <DismissCustomerSheet
        isOpen={dismissOpen}
        onOpenChange={handleDismissOpenChange}
        onConfirm={handleDismissConfirm}
        isSubmitting={dismissResult.fetching}
      />
      <PlanMeetingSheet isOpen={planMeetingOpen} onOpenChange={setPlanMeetingOpen} clientId={clientId} />
      <FullScreenErrorModal
        open={failedAction !== null}
        title={failedAction === "markAsDone" ? t("markAsDone.error") : t("dismiss.error")}
        retryHint={t("retryHint")}
        onDismiss={() => setFailedAction(null)}
      />
    </>
  );
}
